import { EventType } from "@/analytics/analytics-events";
import { EditElementProperties } from "@/analytics/data-organization-events-properties";
import { EditElementNameInput } from "@/modes/sheet-mode/edit-waypoints/edit-waypoint-context-menu/edit-element-name-input";
import { useAppSelector } from "@/store/store-hooks";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import { Box } from "@mui/material";
import { useCallback, type JSX } from "react";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";

type EditNameActionProps = {
  /** The id of the element to edit the name of */
  elementId: string;

  /** Callback to close the context menu after the action is performed */
  closeContextMenu(): void;

  /** Whether to use dark mode styling */
  dark?: boolean;
};

/**
 * Action to edit the name of an element.
 *
 * @returns A menu item that renders a name input field to edit the name of the element.
 */
export function EditNameAction({
  elementId,
  closeContextMenu,
  dark,
}: EditNameActionProps): JSX.Element | null {
  const element = useAppSelector(selectIElement(elementId));

  const onNameEdited = useCallback(() => {
    assert(
      element,
      "Element must be defined to track name editing from the capture tree context menu",
    );
    Analytics.track<EditElementProperties>(EventType.editElement, {
      via: "capture tree context menu",
      elementType: element.type,
      elementTypeHint: element.typeHint ?? undefined,
      hasEditedName: true,
      hasEditedTimePoint: false,
      hasEditedTags: false,
      hasEditedColor: false,
    });

    closeContextMenu();
  }, [closeContextMenu, element]);

  if (!element) return null;

  // The box is used to add padding around the input
  return (
    <Box
      aria-label="edit element name"
      component="div"
      sx={{ pl: 0.75, pr: 0.25, justifyContent: "center" }}
    >
      <EditElementNameInput
        dark={dark}
        element={element}
        fullWidth
        onNameChanged={onNameEdited}
      />
    </Box>
  );
}

/**
 * Action to edit the name of an element.
 * Renders a custom component to show a name input field.
 */
export const EDIT_NAME_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.editName,
  CustomComponent: EditNameAction,
};
